import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { gql, useQuery } from '@apollo/client'

import { LinearProgress, NotificationDialog } from '../components'
import { GiftStepper } from './gift'

// Query to get charity redirect information
const GET_CHARITY_REDIRECT = gql`
  query getCharityRedirect($charityId: String!) {
    charityRedirectByOldCharityId(oldCharityId: $charityId) {
      charityId
      type
    }
  }
`

// Query to get charity details for redirect dialog
const GET_CHARITY_DETAILS = gql`
  query getCharityDetails($charityId: String!) {
    charity(id: $charityId) {
      id
      accountName
      legalName
    }
  }
`

const MAX_REDIRECTS = 5
const REDIRECT_CHAIN_KEY = 'charity_redirect_chain'

/**
 * CharityRedirect Component
 *
 * Purpose: Checks if a charity ID has a redirect, and redirects if found
 *
 * This component wraps charity-related pages and:
 * 1. Checks if the charity ID is redirected to another charity
 * 2. If yes, redirects to the new charity (preserving the URL structure)
 * 3. For fund grant routes, shows a confirmation dialog before redirecting
 * 4. If no redirect, renders the fallback component
 *
 * Implementation notes:
 * - Uses the FundRedirect table with oldCharityId field
 * - For /funds/:fundId/grant/:charityId routes, shows yellow bell alert dialog with charity names
 * - For /gift/charity/:charityId routes, redirects instantly with replace=true
 * - Implements loop detection using sessionStorage to persist across navigations
 * - Works with any URL pattern containing a charityId by replacing it in the current path
 *
 * @param {React.Component} fallback - Component to render when no redirect exists
 * */

export function CharityRedirect({ fallback: FallbackComponent = GiftStepper }) {
  const { charityId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [hasRedirectLoop, setHasRedirectLoop] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [proceedWithRedirect, setProceedWithRedirect] = useState(false)

  // Check if this is a fund grant route (requires confirmation dialog)
  const isFundGrantRoute = location.pathname.includes('/funds/')

  // Reset proceedWithRedirect when charityId changes
  useEffect(() => {
    setProceedWithRedirect(false)
  }, [charityId])

  // Check for redirect loops using sessionStorage
  useEffect(() => {
    if (!charityId) return

    try {
      const chain = JSON.parse(sessionStorage.getItem(REDIRECT_CHAIN_KEY) || '[]')

      if (chain.includes(charityId)) {
        console.error(`Circular redirect detected: ${charityId} has already been visited`)
        sessionStorage.removeItem(REDIRECT_CHAIN_KEY)
        setHasRedirectLoop(true)
        return
      }

      if (chain.length >= MAX_REDIRECTS) {
        console.error(`Too many redirects detected (max: ${MAX_REDIRECTS})`)
        sessionStorage.removeItem(REDIRECT_CHAIN_KEY)
        setHasRedirectLoop(true)
        return
      }

      sessionStorage.setItem(REDIRECT_CHAIN_KEY, JSON.stringify([...chain, charityId]))
    } catch (e) {
      console.error('Error managing redirect chain:', e)
      sessionStorage.removeItem(REDIRECT_CHAIN_KEY)
    }
  }, [charityId])

  // Query to check if this charity ID has a redirect
  const { data, loading, error } = useQuery(GET_CHARITY_REDIRECT, {
    variables: { charityId },
    skip: !charityId || hasRedirectLoop,
  })

  const newCharityId = data?.charityRedirectByOldCharityId?.charityId

  // Query to get old charity details (only when redirect exists and it's a fund grant route)
  const {
    data: oldCharityData,
    loading: loadingOldCharity,
    error: oldCharityError,
  } = useQuery(GET_CHARITY_DETAILS, {
    variables: { charityId },
    skip: !newCharityId || !isFundGrantRoute,
  })

  // Query to get new charity details (only when redirect exists and it's a fund grant route)
  const {
    data: newCharityData,
    loading: loadingNewCharity,
    error: newCharityError,
  } = useQuery(GET_CHARITY_DETAILS, {
    variables: { charityId: newCharityId ?? '' },
    skip: !newCharityId || !isFundGrantRoute,
  })

  // Clear redirect chain when we reach the final destination
  useEffect(() => {
    if (!loading && !data?.charityRedirectByOldCharityId) {
      sessionStorage.removeItem(REDIRECT_CHAIN_KEY)
    }
  }, [loading, data])

  // Show dialog when redirect is detected on fund grant routes
  // Only show if queries succeeded or if we have fallback data (charity IDs)
  useEffect(() => {
    if (
      newCharityId &&
      isFundGrantRoute &&
      !loadingOldCharity &&
      !loadingNewCharity &&
      !proceedWithRedirect &&
      (!oldCharityError || !newCharityError) // Show dialog even if one query failed, as we have fallback IDs
    ) {
      setShowDialog(true)
    }
  }, [
    newCharityId,
    isFundGrantRoute,
    loadingOldCharity,
    loadingNewCharity,
    proceedWithRedirect,
    oldCharityError,
    newCharityError,
  ])

  // Show loading state while checking for redirect
  if (loading || (newCharityId && isFundGrantRoute && (loadingOldCharity || loadingNewCharity))) {
    return <LinearProgress />
  }

  // Handle charity detail query errors - log and proceed with IDs as fallback
  if (oldCharityError) {
    console.error('Error loading old charity details:', oldCharityError)
  }
  if (newCharityError) {
    console.error('Error loading new charity details:', newCharityError)
  }

  // If redirect loop detected, clear chain and show normal page
  if (hasRedirectLoop) {
    sessionStorage.removeItem(REDIRECT_CHAIN_KEY)
    return <FallbackComponent />
  }

  // If there's a redirect
  if (newCharityId) {
    // For fund grant routes, show dialog first (unless user already confirmed)
    if (isFundGrantRoute && !proceedWithRedirect) {
      const oldCharity = oldCharityData?.charity
      const newCharity = newCharityData?.charity

      const oldCharityName = oldCharity?.legalName || oldCharity?.accountName || charityId
      const newCharityName = newCharity?.legalName || newCharity?.accountName || newCharityId

      const dialogMessage = `Please be aware that ${oldCharityName} (${charityId}) has requested that all gifts be directed through ${newCharityName} (${newCharityId}).\n\nFor more information, please reach out to the charity.`

      const handleContinue = () => {
        setShowDialog(false)
        sessionStorage.removeItem(REDIRECT_CHAIN_KEY)
        setProceedWithRedirect(true)
      }

      const handleReturnToSearch = () => {
        setShowDialog(false)
        sessionStorage.removeItem(REDIRECT_CHAIN_KEY)
        // Navigate back to the grant search page
        navigate('../grant', { replace: true })
      }

      return (
        <NotificationDialog
          open={showDialog}
          onClose={handleReturnToSearch}
          type="alert"
          title="Gift Request Redirection"
          message={dialogMessage}
          actions={[
            { label: 'CREATE GIFT', onClick: handleContinue, variant: 'contained' },
            { label: 'RETURN TO SEARCH', onClick: handleReturnToSearch },
          ]}
        />
      )
    }

    // For gift routes or after user confirmed, proceed with redirect
    // Replace last occurrence of charityId to avoid replacing fundId if it matches
    const pathSegments = location.pathname.split('/')
    const charityIdIndex = pathSegments.lastIndexOf(charityId)
    if (charityIdIndex !== -1) {
      pathSegments[charityIdIndex] = newCharityId
    }
    const newPath = pathSegments.join('/')
    return <Navigate to={newPath} replace />
  }

  // Display error message if query failed
  if (error) {
    console.error('Error checking for charity redirect:', error)
    sessionStorage.removeItem(REDIRECT_CHAIN_KEY)
  }

  // No redirect found (or error), render the fallback component
  return <FallbackComponent />
}
